import { useState, useCallback } from 'react';

/**
 * Hook that wraps useState with localStorage persistence.
 * Safely handles environments where localStorage is unavailable.
 */
export function useLocalStorage<T>(
  key: string,
  initialValue: T,
  serialize: (value: T) => string = JSON.stringify,
  deserialize: (value: string) => T = JSON.parse,
): [T, (value: T | ((prev: T) => T)) => void] {
  const [storedValue, setStoredValue] = useState<T>(() => {
    try {
      const item = localStorage.getItem(key);
      return item !== null ? deserialize(item) : initialValue;
    } catch {
      return initialValue;
    }
  });

  const setValue = useCallback(
    (value: T | ((prev: T) => T)) => {
      setStoredValue((prev) => {
        const next = value instanceof Function ? value(prev) : value;
        try {
          localStorage.setItem(key, serialize(next));
        } catch {
          // Ignore localStorage errors
        }
        return next;
      });
    },
    [key, serialize],
  );

  return [storedValue, setValue];
}
